import { cn } from '@brew/ui/cn'
import { ArrowUpRight } from 'lucide-react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'

type CommonProps = {
  children: ReactNode
  className?: string
}

type AnchorProps = CommonProps & {
  href: string
  /**
   * Runs once hydrated (it may `preventDefault` to act in place, e.g. open a
   * modal); before that, or without JavaScript, the link simply navigates.
   */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  type?: never
  target?: string
  rel?: string
}

type ButtonProps = CommonProps & {
  href?: never
  onClick: () => void
  type?: 'button' | 'submit'
  target?: never
  rel?: never
}

export type ProminentCtaButtonProps = AnchorProps | ButtonProps

/**
 * The brand pill from `HomeButton` (`variant: 'brand'`: 40px, 70px radius,
 * the orange gradient, a 1.5px ink border and the 1px button shadow) with a
 * shimmer sweeping across it. `--bg` is the same gradient every other orange
 * CTA paints, so this one differs only by the sheen.
 */
const SHIMMER_STYLE = {
  '--spread': '90deg',
  '--shimmer-color': '#ffffff',
  '--radius': '70px',
  '--speed': '3s',
  '--cut': '0.05em',
  '--bg': 'var(--mk-brand-gradient)',
} as CSSProperties

const PROMINENT_CTA_CLASSES = cn(
  'group relative z-0 inline-flex h-10 shrink-0 cursor-pointer items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap border-[1.5px] border-mk-brand-border px-5 font-medium font-mk-body text-[14px] text-white leading-5 tracking-[-0.01em] shadow-mk-button [background:var(--bg)] [border-radius:var(--radius)]',
  'transition-[filter] duration-fast ease-smooth-out hover:brightness-105 focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2 motion-reduce:transition-none'
)

function ProminentCtaButtonContent({ children }: { children: ReactNode }) {
  return (
    <>
      <span className="absolute inset-0 -z-30 overflow-visible blur-[2px] [container-type:size]">
        <span className="absolute inset-0 h-[100cqh] animate-shimmer-slide [aspect-ratio:1] [border-radius:0] [mask:none] motion-reduce:animate-none">
          <span className="absolute -inset-full w-auto rotate-0 animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))] motion-reduce:animate-none" />
        </span>
      </span>
      <span className="absolute -z-20 [background:var(--bg)] [border-radius:var(--radius)] [inset:var(--cut)]" />
      <span className="relative">{children}</span>
      <ArrowUpRight className="relative size-4" aria-hidden />
    </>
  )
}

export function ProminentCtaButton(props: ProminentCtaButtonProps) {
  const className = cn(PROMINENT_CTA_CLASSES, props.className)

  if (props.href !== undefined) {
    return (
      <a
        href={props.href}
        onClick={props.onClick}
        target={props.target}
        rel={props.rel}
        style={SHIMMER_STYLE}
        className={className}
      >
        <ProminentCtaButtonContent>{props.children}</ProminentCtaButtonContent>
      </a>
    )
  }

  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      style={SHIMMER_STYLE}
      className={className}
    >
      <ProminentCtaButtonContent>{props.children}</ProminentCtaButtonContent>
    </button>
  )
}
